import React, { useContext, useState, useEffect, useRef } from "react";
import { subscribeChatEvents, CHAT_CHANGES, eventRefresh } from '../../../services/chatEvents';
import { receiveChatActivity, unlockChatSound } from '../../../services/chatSound';
import {
  List,
  ListItem,
  ListItemText,
  Badge,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsIcon from "@mui/icons-material/Settings"; // Для настроек
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount"; // Иконка для сотрудников
import HandshakeIcon from "@mui/icons-material/Handshake"; // Иконка для сделок
import TrendingDownIcon from "@mui/icons-material/TrendingDown"; // Иконка для расходов
import AnalyticsIcon from "@mui/icons-material/Analytics"; // Иконка для аналитики
import ChatIcon from "@mui/icons-material/Chat"; // Иконка для мессенджера
import AssignmentIcon from "@mui/icons-material/Assignment"; // Иконка для заявок
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"; // Иконка для закупок
import StorefrontIcon from "@mui/icons-material/Storefront"; // Иконка для витрины
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ThemeContext } from "../context/ThemeContext"; // Подключаем ThemeContext
import api from "../../api/api";

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => !["sidebarcolor", "active", "collapsed"].includes(prop),
})(({ theme, sidebarcolor, active, collapsed }) => {
  // Определяем, светлый ли фон
  const isLightBackground =
    sidebarcolor &&
    (sidebarcolor.toLowerCase().includes("#fff") ||
      sidebarcolor.toLowerCase().includes("#f") ||
      sidebarcolor.toLowerCase() === "white" ||
      sidebarcolor.toLowerCase().includes("rgb(255") ||
      sidebarcolor.toLowerCase().includes("f5f5f5"));

  return {
    color: isLightBackground ? "#333" : "white",
    borderRadius: "10px",
    margin: "2px 8px",
    width: "calc(100% - 16px)",
    transition: "all 0.2s ease",
    backgroundColor: active
      ? isLightBackground
        ? "rgba(0,0,0,0.1)"
        : "rgba(255,255,255,0.18)"
      : "transparent",
    "&:hover": {
      backgroundColor: isLightBackground
        ? "rgba(0,0,0,0.08)"
        : "rgba(255,255,255,0.12)",
      color: isLightBackground ? "#000" : "white",
    },
    fontSize: "16px",
    justifyContent: collapsed ? "center" : "flex-start",
    padding: collapsed ? "10px" : "10px 16px",
    "& .MuiListItemText-root": {
      display: collapsed ? "none" : "block",
    },
    "& .MuiSvgIcon-root": {
      marginRight: collapsed ? "0 !important" : undefined,
    },
  };
});

const Sidebar = ({ collapsed = false, onToggle = () => {} }) => {
  const { theme } = useContext(ThemeContext); // Берем текущую тему из контекста
  const sidebarColor = !theme.sidebarColor || theme.sidebarColor.toLowerCase() === "#f5f5f5" ? "#192b3e" : theme.sidebarColor;
  const location = useLocation();
  const alive = useRef(false);
  const requestVersion = useRef({unread: 0, applications: 0});
  const [unreadCount, setUnreadCount] = useState(0);
  const [applicationsCount, setApplicationsCount] = useState(0);

  // Определяем, светлый ли фон для иконок
  const isLightBackground =
    sidebarColor &&
    (sidebarColor.toLowerCase().includes("#fff") ||
      sidebarColor.toLowerCase().includes("#f") ||
      sidebarColor.toLowerCase() === "white" ||
      sidebarColor.toLowerCase().includes("rgb(255") ||
      sidebarColor.toLowerCase().includes("f5f5f5"));

  const iconColor = isLightBackground ? "#333" : "white";

  // Загрузка количества непрочитанных
  const loadUnreadCount = async () => {
    const version = ++requestVersion.current.unread;
    try {
      const response = await api.get("/chats/activity", { skipLoading: true });
      if (alive.current && version === requestVersion.current.unread) {
        setUnreadCount(Math.max(0, Math.floor(Number(response.data.unread_count) || 0)));
        receiveChatActivity(response.data);
      }
    } catch (err) {
      return false;
    }
  };

  // Загрузка количества заявок
  const loadApplicationsCount = async () => {
    const version = ++requestVersion.current.applications;
    try {
      const response = await api.get("/applications/count", { skipLoading: true });
      if (alive.current && version === requestVersion.current.applications) setApplicationsCount(Math.max(0, Math.floor(Number(response.data.count) || 0)));
    } catch (err) {
      // Не критично — бейдж просто не покажется
    }
  };

  useEffect(() => {
    alive.current = true;
    loadUnreadCount();
    loadApplicationsCount();
    // Обновляем счётчик заявок каждые 30 секунд
    const interval = setInterval(loadApplicationsCount, 30000);
    const refreshChats = eventRefresh(loadUnreadCount);
    window.addEventListener('pointerdown', unlockChatSound);
    window.addEventListener('keydown', unlockChatSound);
    window.addEventListener('staff-chat-read', loadUnreadCount);

    const unsubscribe = subscribeChatEvents('staff', localStorage.getItem('token') || '', event => {
      if (CHAT_CHANGES.has(event.type)) refreshChats.request();
      if (['ready', 'resync', 'new_application'].includes(event.type)) loadApplicationsCount();
    });

    return () => {
      alive.current = false;
      clearInterval(interval);
      refreshChats.dispose();
      window.removeEventListener('pointerdown', unlockChatSound);
      window.removeEventListener('keydown', unlockChatSound);
      window.removeEventListener('staff-chat-read', loadUnreadCount);
      unsubscribe();
    };
  }, []);

  return (
    <div className="erp-sidebar"
      style={{
        "--erp-sidebar-bg": sidebarColor,
        "--erp-sidebar-ink": iconColor,
        position: "fixed",
        top: "64px", // Отступ под Navbar (стандартная высота Toolbar)
        left: 0,
        width: collapsed ? "72px" : "200px",
        background: sidebarColor || "#f5f5f5", // Используем цвет из темы или светло-серый дефолтный
        height: "calc(100vh - 64px)", // Высота экрана минус высота Navbar
        padding: "0px",
        zIndex: 1200, // Выше контента, но ниже Navbar
        overflowY: "auto", // Добавляем прокрутку если много элементов
        transition: "width 180ms ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-end",
          px: 1,
          pt: 0.75,
        }}
      >
        <Tooltip title={collapsed ? "Развернуть меню" : "Свернуть меню"} placement="right">
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={collapsed ? "Развернуть боковое меню" : "Свернуть боковое меню"}
            sx={{ color: iconColor }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <List sx={{ pt: 0.5, pb: 1 }}>
        {/* Группа: Основное */}
        <Typography
          variant="caption"
          sx={{
            px: 2,
            pb: 0.5,
            display: collapsed ? "none" : "block",
            opacity: 0.45,
            color: iconColor,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "10px",
            fontWeight: 600,
          }}
        >
          Основное
        </Typography>

        <StyledListItem
          component={Link}
          to="/dashboard"
          aria-current={location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/dashboard" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Дашборд"
          aria-label="Дашборд"
        >
          <DashboardIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Дашборд" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/returns"
          aria-current={location.pathname === "/returns" || location.pathname.startsWith("/returns/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/returns" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Возвраты"
          aria-label="Возвраты"
        >
          <AssignmentReturnIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Возвраты" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/rental-deals"
          aria-current={location.pathname === "/rental-deals" || location.pathname.startsWith("/rental-deals/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname.startsWith("/rental-deals") ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Сделки"
          aria-label="Сделки"
        >
          <HandshakeIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Сделки" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/avito-orders"
          aria-current={location.pathname === "/avito-orders" || location.pathname.startsWith("/avito-orders/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname.startsWith("/avito-orders") ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Avito Заказы"
          aria-label="Avito Заказы"
        >
          <LocalShippingIcon
            style={{ marginRight: "10px", color: iconColor }}
          />
          <ListItemText primary="Avito Заказы" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/applications"
          aria-current={location.pathname === "/applications" || location.pathname.startsWith("/applications/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/applications" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Заявки"
          aria-label="Заявки"
        >
          <Badge badgeContent={applicationsCount} color="warning" max={99}>
            <AssignmentIcon style={{ marginRight: "10px", color: iconColor }} />
          </Badge>
          <ListItemText primary="Заявки" style={{ marginLeft: "10px" }} />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/deals"
          aria-current={location.pathname === "/deals" || location.pathname.startsWith("/deals/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/deals" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Продажи"
          aria-label="Продажи"
        >
          <PeopleIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Продажи" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/clients"
          aria-current={location.pathname === "/clients" || location.pathname.startsWith("/clients/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/clients" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Клиенты"
          aria-label="Клиенты"
        >
          <PeopleIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Клиенты" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/messenger"
          aria-current={location.pathname === "/messenger" || location.pathname.startsWith("/messenger/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/messenger" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Мессенджер"
          aria-label="Мессенджер"
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <ChatIcon style={{ marginRight: "10px", color: iconColor }} />
          </Badge>
          <ListItemText primary="Мессенджер" style={{ marginLeft: "10px" }} />
        </StyledListItem>

        {/* Разделитель */}
        <Divider
          sx={{
            my: 1,
            mx: collapsed ? 1 : 2,
            borderColor: isLightBackground
              ? "rgba(0,0,0,0.12)"
              : "rgba(255,255,255,0.12)",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            px: 2,
            pb: 0.5,
            display: collapsed ? "none" : "block",
            opacity: 0.45,
            color: iconColor,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "10px",
            fontWeight: 600,
          }}
        >
          Активы и финансы
        </Typography>
        <StyledListItem
          component={Link}
          to="/assets"
          aria-current={location.pathname === "/assets" || location.pathname.startsWith("/assets/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/assets" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Активы"
          aria-label="Активы"
        >
          <Inventory2OutlinedIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Активы" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/courier"
          aria-current={location.pathname === "/courier" ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/courier" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Курьер"
          aria-label="Курьер"
        >
          <LocalShippingIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Курьер" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/showcase"
          aria-current={location.pathname === "/showcase" || location.pathname.startsWith("/showcase/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/showcase" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Витрина товаров"
          aria-label="Витрина товаров"
        >
          <StorefrontIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Витрина товаров" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/reports"
          aria-current={location.pathname === "/reports" || location.pathname.startsWith("/reports/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/reports" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Отчеты"
          aria-label="Отчеты"
        >
          <AssessmentOutlinedIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Отчеты" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/analytics"
          aria-current={location.pathname === "/analytics" || location.pathname.startsWith("/analytics/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/analytics" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Аналитика"
          aria-label="Аналитика"
        >
          <AnalyticsIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Аналитика" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/expenses"
          aria-current={location.pathname === "/expenses" || location.pathname.startsWith("/expenses/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/expenses" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Расходы"
          aria-label="Расходы"
        >
          <TrendingDownIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Расходы" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/procurement"
          aria-current={location.pathname === "/procurement" || location.pathname.startsWith("/procurement/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/procurement" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Закупки"
          aria-label="Закупки"
        >
          <ShoppingCartIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Закупки" />
        </StyledListItem>

        {/* Разделитель */}
        <Divider
          sx={{
            my: 1,
            mx: collapsed ? 1 : 2,
            borderColor: isLightBackground
              ? "rgba(0,0,0,0.12)"
              : "rgba(255,255,255,0.12)",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            px: 2,
            pb: 0.5,
            display: collapsed ? "none" : "block",
            opacity: 0.45,
            color: iconColor,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "10px",
            fontWeight: 600,
          }}
        >
          Система
        </Typography>

        <StyledListItem
          component={Link}
          to="/settings"
          aria-current={location.pathname === "/settings" || location.pathname.startsWith("/settings/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/settings" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Настройки"
          aria-label="Настройки"
        >
          <SettingsIcon style={{ marginRight: "10px", color: iconColor }} />
          <ListItemText primary="Настройки" />
        </StyledListItem>
        <StyledListItem
          component={Link}
          to="/staff"
          aria-current={location.pathname === "/staff" || location.pathname.startsWith("/staff/") ? "page" : undefined}
          sidebarcolor={sidebarColor}
          active={location.pathname === "/staff" ? 1 : 0}
          collapsed={collapsed ? 1 : 0}
          title="Сотрудники"
          aria-label="Сотрудники"
        >
          <SupervisorAccountIcon
            style={{ marginRight: "10px", color: iconColor }}
          />
          <ListItemText primary="Сотрудники" />
        </StyledListItem>
      </List>
    </div>
  );
};

export default Sidebar;
